import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import type {
  AdapterHealthV1,
  AdapterStatus,
  HealthV1Response,
  TerritorialHealthV1,
} from '../lib/types';

const REFRESH_MS = 60_000;

/**
 * Dashboard de salud para los adapters de scraping (TK-S02-04).
 *
 * Cada adapter aparece como una card con:
 *  - status (OK / STALE / ERROR) coloreado
 *  - timestamp humano de última corrida exitosa ("hace 4 horas")
 *  - lag en horas
 *  - error message si lo hay
 *  - stats de la última corrida (fetched / new / updated)
 *
 * Auto-refresh cada 60 segundos para que un usuario que deje la pestaña
 * abierta vea los cambios cuando los crons corren. Sin ws/SSE — pull
 * es perfectamente adecuado para un dashboard de N adapters.
 */
export function HealthDashboard() {
  const [data, setData] = useState<HealthV1Response | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    function load() {
      const ctrl = new AbortController();
      api
        .health(ctrl.signal)
        .then((r) => {
          if (cancelled) return;
          setData(r);
          setError(null);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : 'Error desconocido');
        });
      return ctrl;
    }

    let activeCtrl = load();
    timer = window.setInterval(() => {
      activeCtrl.abort();
      activeCtrl = load();
    }, REFRESH_MS);

    return () => {
      cancelled = true;
      activeCtrl.abort();
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, []);

  if (error && !data) {
    return <div className="empty">{error}</div>;
  }
  if (!data) {
    return <div className="empty">cargando…</div>;
  }

  return (
    <div className="health-dashboard">
      <SummaryRow data={data} />
      <div className="health-list">
        {data.adapters.length === 0 && (
          <div className="empty">sin adapters registrados</div>
        )}
        {data.adapters.map((a) => (
          <AdapterCard key={a.sourceType} adapter={a} />
        ))}
      </div>
      {data.territorial && data.territorial.length > 0 && (
        <>
          <div
            className="health-section-divider"
            title="Datasets territoriales (refresh mensual): SESNSP incidencia delictiva, MOFFIN, etc. Threshold más laxo que adapters (60 días)."
          >
            Territoriales (cron mensual)
          </div>
          <div className="health-list">
            {data.territorial.map((t) => (
              <TerritorialCard key={t.sourceType} item={t} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryRow({ data }: { data: HealthV1Response }) {
  const { summary } = data;
  const degraded = summary.degraded ?? 0;
  return (
    <div className="health-summary" title={`DB ${data.db ? 'OK' : 'caída'} · umbral ${data.thresholdHours}h`}>
      <span className={`hd-pill is-ok ${summary.ok > 0 ? 'is-on' : ''}`}>
        ✓ {summary.ok} OK
      </span>
      {/* RFC-0021 A2: corridas con volumen anómalo (fetch < 50% de su
          mediana histórica) — scrape probablemente roto aunque no lance. */}
      {degraded > 0 && (
        <span className="hd-pill is-degraded is-on">
          ▽ {degraded} DEGRADED
        </span>
      )}
      <span className={`hd-pill is-stale ${summary.stale > 0 ? 'is-on' : ''}`}>
        ◐ {summary.stale} STALE
      </span>
      <span className={`hd-pill is-error ${summary.error > 0 ? 'is-on' : ''}`}>
        ✕ {summary.error} ERROR
      </span>
    </div>
  );
}

function AdapterCard({ adapter }: { adapter: AdapterHealthV1 }) {
  const ago = useMemo(() => formatAgo(adapter.lastSuccessAt), [adapter.lastSuccessAt]);
  const tooltip = useMemo(() => buildTooltip(adapter), [adapter]);
  const displayName = useMemo(() => adapterDisplayName(adapter.sourceType), [adapter.sourceType]);

  return (
    <div
      className={`health-card status-${adapter.status.toLowerCase()}`}
      title={tooltip}
    >
      <div className="hc-row">
        <span className={`hc-status status-${adapter.status.toLowerCase()}`}>
          {adapter.status}
        </span>
        <span className="hc-name">{displayName}</span>
        {adapter.manualLoad && (
          <span
            className="hc-manual"
            title="Inventario cargado manualmente — la fuente no corre en el cron; la antigüedad mostrada es real y esperada."
          >
            carga manual
          </span>
        )}
        {adapter.lastRunPartial && !adapter.manualLoad && (
          <span
            className="hc-manual hc-partial"
            title="La última corrida tuvo fallos de fetch y conservó solo parte del catálogo. Lo capturado se guardó; el deslistado automático ignora corridas parciales."
          >
            parcial
          </span>
        )}
      </div>
      <div className="hc-meta">
        <span className="hc-ago">
          {ago === null ? 'nunca corrió' : `último éxito ${ago}`}
        </span>
        {adapter.recordsFetched !== null && (
          <span className="hc-stats">
            {adapter.recordsFetched.toLocaleString('es-MX')} obtenidos
            {adapter.recordsNew !== null && adapter.recordsNew > 0 &&
              ` · +${adapter.recordsNew.toLocaleString('es-MX')} nuevos`}
          </span>
        )}
      </div>
      {adapter.lastError && (
        <div className="hc-error" title={adapter.lastError}>
          {truncate(adapter.lastError, 90)}
        </div>
      )}
    </div>
  );
}

/**
 * Mapeo sourceType (constante backend) → etiqueta legible para el dashboard.
 * Sin entrada explícita, devuelve el sourceType original como fallback
 * sano (útil mientras se conectan adapters nuevos sin redeploy del FE).
 */
const ADAPTER_LABELS: Record<string, string> = {
  BBVA_REO: 'BBVA — REO bancario',
  BANORTE_REO: 'Banorte — REO bancario',
  BANREGIO_REO: 'Banregio — REO bancario',
  INDEP_SUBASTA: 'INDEP — Subastas judiciales',
  SHF_PENDULUM: 'Pendulum — REO multi-cartera (SHF + 11 más)',
  HSBC_REO: 'HSBC México — Bienes adjudicados',
  SESNSP_INCIDENCIA: 'SESNSP — Incidencia delictiva',
};

function adapterDisplayName(sourceType: string): string {
  return ADAPTER_LABELS[sourceType] ?? sourceType;
}

function TerritorialCard({ item }: { item: TerritorialHealthV1 }) {
  const ago = useMemo(() => formatAgo(item.lastLoadedAt), [item.lastLoadedAt]);
  const periodPretty = useMemo(() => prettyPeriod(item.latestPeriod), [item.latestPeriod]);
  const tooltip = [
    `Status: ${item.status}`,
    item.ageDays !== null ? `Edad: ${item.ageDays.toFixed(1)} días` : 'Sin cargas',
    `Última carga: ${item.lastLoadedAt ?? 'never'}`,
    item.latestPeriod ? `Periodo: ${item.latestPeriod}` : '',
    item.coverageMunicipios !== null ? `Municipios: ${item.coverageMunicipios.toLocaleString('es-MX')}` : '',
  ].filter(Boolean).join('\n');

  return (
    <div className={`health-card status-${item.status.toLowerCase().replace('_', '-')}`} title={tooltip}>
      <div className="hc-row">
        <span className={`hc-status status-${item.status.toLowerCase().replace('_', '-')}`}>
          {item.status === 'NOT_LOADED' ? 'NO DATA' : item.status}
        </span>
        <span className="hc-name">{adapterDisplayName(item.sourceType)}</span>
      </div>
      <div className="hc-meta">
        {item.lastLoadedAt
          ? <span className="hc-ago">cargado {ago}</span>
          : <span className="hc-ago">sin cargas — ver `scripts/load-sesnsp.ts`</span>}
        {item.coverageMunicipios !== null && item.coverageMunicipios > 0 && (
          <span className="hc-stats">
            {item.coverageMunicipios.toLocaleString('es-MX')} municipios
            {periodPretty && ` · ${periodPretty}`}
          </span>
        )}
      </div>
    </div>
  );
}

function prettyPeriod(period: string | null): string | null {
  if (!period || !/^\d{6}$/.test(period)) return null;
  const year = period.slice(0, 4);
  const monthNum = Number(period.slice(4));
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  if (monthNum < 1 || monthNum > 12) return null;
  return `${months[monthNum - 1]} ${year}`;
}

function buildTooltip(a: AdapterHealthV1): string {
  const parts = [
    `Status: ${a.status}`,
    `Lag: ${a.lagHours === null ? 'n/d' : `${a.lagHours.toFixed(1)} h`}`,
    `Last success: ${a.lastSuccessAt ?? 'never'}`,
    `Last run: ${a.lastRunAt ?? 'never'}`,
  ];
  if (a.status === 'DEGRADED' && a.medianFetched7 != null) {
    parts.push(
      `Volumen anómalo: trajo ${a.recordsFetched?.toLocaleString('es-MX') ?? '?'} vs mediana ${a.medianFetched7.toLocaleString('es-MX')} de las últimas 7 corridas — scrape posiblemente roto.`,
    );
  }
  if (a.manualLoad) parts.push('Carga manual: no corre en el cron.');
  if (a.lastError) parts.push(`Error: ${a.lastError}`);
  return parts.join('\n');
}

const RTF = typeof Intl !== 'undefined' && 'RelativeTimeFormat' in Intl
  ? new Intl.RelativeTimeFormat('es-MX', { numeric: 'auto' })
  : null;

function formatAgo(iso: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const deltaSec = Math.round((then - Date.now()) / 1000);
  const absSec = Math.abs(deltaSec);
  if (!RTF) return new Date(iso).toLocaleString('es-MX');
  if (absSec < 60) return RTF.format(deltaSec, 'second');
  if (absSec < 3600) return RTF.format(Math.round(deltaSec / 60), 'minute');
  if (absSec < 86_400) return RTF.format(Math.round(deltaSec / 3600), 'hour');
  return RTF.format(Math.round(deltaSec / 86_400), 'day');
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}

/**
 * Variante mínima inline-friendly del status para el HealthBadge en el
 * header. Pequeña píldora con el peor status global.
 */
export type AdapterRollupStatus = AdapterStatus | 'LOADING';

export function rollupStatus(adapters: AdapterHealthV1[]): AdapterStatus {
  if (adapters.some((a) => a.status === 'ERROR')) return 'ERROR';
  if (adapters.some((a) => a.status === 'STALE')) return 'STALE';
  if (adapters.some((a) => a.status === 'DEGRADED')) return 'DEGRADED';
  return 'OK';
}
